import React, { useCallback, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdChevronRight } from 'react-icons/md';
import { ToolChipSpinner } from './Chat.styled';
import {
  TrailBody,
  TrailChevron,
  TrailFailed,
  TrailHeader,
  TrailMeta,
  TrailTitle,
  TrailWrapper,
} from './ToolTrail.styled';
import { formatElapsed, useElapsedSeconds } from './elapsed';
import { useToolDisplayName } from './toolDisplayName';
import { useSkillLoadLabel } from './skillLoadLabel';
import type { SetupStep, ToolExecution } from '@hooks/useChat';

const toMs = (d: Date | string | undefined): number | undefined => {
  if (d === undefined) return undefined;
  const ms = new Date(d).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

const turnSeconds = (
  start: number | undefined,
  end: number | undefined,
  running: boolean,
  live: number,
): number | undefined => {
  if (start === undefined) return undefined;
  if (running) return live;
  if (end === undefined) return undefined;
  return Math.max(0, Math.floor((end - start) / 1000));
};

const currentStep = (steps: ToolExecution[]): ToolExecution | undefined => {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].status === 'executing') return steps[i];
  }
  return undefined;
};

const titleKey = (active: boolean, timed: boolean): string => {
  if (active) return timed ? 'chat.trail.working' : 'chat.trail.workingUntimed';
  return timed ? 'chat.trail.worked' : 'chat.trail.workedUntimed';
};

interface ToolTrailProps {
  steps: ToolExecution[];
  setupSteps?: SetupStep[];
  active: boolean;
  awaitingApproval?: boolean;
  startedAt?: Date | string;
  completedAt?: Date | string;
  children: React.ReactNode;
}

const ToolTrail: React.FC<ToolTrailProps> = ({
  steps,
  setupSteps,
  active,
  awaitingApproval = false,
  startedAt,
  completedAt,
  children,
}) => {
  const intl = useIntl();
  const getDisplayName = useToolDisplayName();
  const bodyId = useId();
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);
  const start = toMs(startedAt);
  const live = useElapsedSeconds(start, active);
  const seconds = turnSeconds(start, toMs(completedAt), active, live);
  const elapsed = seconds === undefined ? undefined : formatElapsed(seconds);
  const failed = steps.filter((s) => s.status === 'complete' && s.success === false).length;
  const activeSetup = setupSteps?.find((s) => s.status === 'active');
  const skillLabel = useSkillLoadLabel(activeSetup?.kind === 'skill' ? activeSetup.skill : undefined, false);

  const liveLabel = (): string => {
    if (awaitingApproval) return intl.formatMessage({ id: 'chat.trail.awaitingApproval' });
    if (activeSetup?.kind === 'environment') return intl.formatMessage({ id: 'chat.trail.environment' });
    if (activeSetup) return skillLabel;
    const step = currentStep(steps);
    if (!step) return intl.formatMessage({ id: 'chat.thinking' });
    const name = getDisplayName(step.tool);
    return step.summary ? `${name} — ${step.summary}` : name;
  };

  return (
    <TrailWrapper data-testid="tool-trail">
      <TrailHeader type="button" onClick={toggle} aria-expanded={expanded} aria-controls={bodyId}>
        {active && <ToolChipSpinner />}
        <TrailTitle>
          {intl.formatMessage({ id: titleKey(active, elapsed !== undefined) }, { elapsed })}
        </TrailTitle>
        <TrailMeta>
          {'· '}
          {active
            ? liveLabel()
            : intl.formatMessage({ id: 'chat.trail.steps' }, { count: steps.length })}
          {!active && failed > 0 && (
            <TrailFailed>
              {' · '}
              {intl.formatMessage({ id: 'chat.trail.failed' }, { count: failed })}
            </TrailFailed>
          )}
        </TrailMeta>
        <TrailChevron $expanded={expanded}>
          <MdChevronRight />
        </TrailChevron>
      </TrailHeader>
      <TrailBody id={bodyId} hidden={!expanded} data-testid="tool-trail-body">
        {children}
      </TrailBody>
    </TrailWrapper>
  );
};

export default ToolTrail;
